import React from 'react';
import CargomaticCard from 'components/v2/card';
import ChevronUp from 'components/v2/icons/ChevronUpIcon';
import ChevronDown from 'components/v2/icons/ChevronDownIcon';
import EmailIcon from 'components/v2/icons/EmailIcon';
import { headersByFieldNames } from 'components/modules/accounting/payouts/mappings';
import AccountingActionCreators from 'actions/AccountingActionCreators';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import { withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import { PayoutNumberCell, DateCell, BooleanCell, PayoutStatusCell, RawCell, MoneyCell, ActionsCell, PayoutCheckBoxCell, } from './renderer';
import Checkbox from '../../../v2/forms/Checkbox';
import SessionStore from '../../../../stores/SessionStore';
import SendRateCardEmailModal from 'components/modules/user/rates/SendRateCardEmailModal';
const ACTIONS = {
    APPROVE: 'approve',
    REVERSE_APPROVAL: 'reverse approval',
    VOID: 'void',
    REVERSE_VOID: 'reverse void',
    BULK_STRIPE_PAYOUT: 'bulk stripe payout',
};
const STATUS = {
    APPROVED_INTERNALLY: 'approved_internally',
    PAYMENT_INITIALIZED: 'payment_initialized',
    PAID: 'paid',
    VOID: 'void',
};
const getDisableActionsState = (action, status) => {
    switch (action) {
        case ACTIONS.APPROVE:
            return [STATUS.PAYMENT_INITIALIZED, STATUS.PAID, STATUS.VOID].includes(status);
        case ACTIONS.VOID:
            return [
                STATUS.APPROVED_INTERNALLY,
                STATUS.PAYMENT_INITIALIZED,
                STATUS.PAID,
            ].includes(status);
        case ACTIONS.BULK_STRIPE_PAYOUT:
            return [STATUS.APPROVED_INTERNALLY].includes(status);
        default:
            return false;
    }
};
class PayoutList extends React.Component {
    constructor(props) {
        super(props);
        this.onApproval = () => {
            const { currentPayout: { _id, status }, } = this.state;
            if (status === STATUS.APPROVED_INTERNALLY) {
                return this.setState({ showConfirmModal: false }, () => AccountingActionCreators.reversePayoutApproval(_id));
            }
            return this.setState({ showConfirmModal: false }, () => AccountingActionCreators.approvePayout(_id));
        };
        this.onVoid = () => {
            const { currentPayout: { _id, status }, } = this.state;
            if (status === STATUS.VOID) {
                return this.setState({ showConfirmModal: false }, () => AccountingActionCreators.reversePayoutVoid(_id));
            }
            return this.setState({ showConfirmModal: false }, () => AccountingActionCreators.voidPayout(_id));
        };
        this.onCancelClick = () => {
            this.setState({
                showConfirmModal: false,
                confirmAction: '',
                currentPayout: null,
            });
        };
        this.onConfirmClick = () => {
            switch (this.state.confirmAction) {
                case ACTIONS.APPROVE:
                case ACTIONS.REVERSE_APPROVAL:
                    return this.onApproval();
                case ACTIONS.VOID:
                case ACTIONS.REVERSE_VOID:
                    return this.onVoid();
                default:
                    break;
            }
        };
        this.getSelectables = () => {
            return this.props.payouts.filter((payout) => getDisableActionsState(ACTIONS.BULK_STRIPE_PAYOUT, payout.status) &&
                payout.auto_pay_enabled);
        };
        this.isAllItemsChecked = () => {
            const selectables = this.getSelectables();
            return (selectables.length > 0 &&
                this.state.selectedPayouts.length === selectables.length);
        };
        this.handleToggleCheckAll = () => {
            const selectables = this.getSelectables();
            if (this.state.selectedPayouts.length === selectables.length) {
                this.setState({ selectedPayouts: [] });
            }
            else {
                this.setState({
                    selectedPayouts: selectables.map((payout) => payout._id),
                });
            }
        };
        this.handleToggleCheck = (payoutId) => {
            this.setState((prev) => {
                let selectedPayouts = [...prev.selectedPayouts];
                const index = selectedPayouts.indexOf(payoutId);
                if (index === -1) {
                    selectedPayouts.push(payoutId);
                }
                else {
                    selectedPayouts.splice(index, 1);
                }
                return { selectedPayouts };
            });
        };
        this.disableLineItem = (payout) => {
            const { status, auto_pay_enabled } = payout;
            return (!getDisableActionsState(ACTIONS.BULK_STRIPE_PAYOUT, status) ||
                !this.state.isFinanceUser ||
                !auto_pay_enabled);
        };
        this.updatePayoutReportEmailModalState = (modalVisibility) => {
            this.setState((prevState) => ({
                modal: Object.assign(Object.assign({}, prevState.modal), { showPayoutReportEmailModal: modalVisibility }),
            }));
        };
        this.handleEmailSubmit = (recipients) => {
            this.props.handleSendPayoutsReportToEmail(recipients);
        };
        this.state = {
            sort: {
                key: props.sort.key,
                order: props.sort.order,
                showConfirmModal: false,
                confirmAction: '',
                currentPayout: null,
            },
            selectedPayouts: [],
            isFinanceUser: SessionStore.isFinanceAdmin(),
            modal: {
                showPayoutReportEmailModal: false,
            },
        };
    }
    getPayoutReportCardEmailRecipientList() {
        var _a;
        const loggedInUserEmail = ((_a = SessionStore.getUser()) === null || _a === void 0 ? void 0 : _a.email) || '';
        return [loggedInUserEmail].filter(Boolean).join(', ').trim();
    }
    onHeaderClick(key) {
        const state = Object.assign({}, this.state);
        if (key === this.state.sort.key) {
            state.sort.order = state.sort.order === 'asc' ? 'desc' : 'asc';
        }
        else {
            state.sort = {
                key,
                order: 'asc',
            };
        }
        this.props.onSortChange(state.sort);
        this.setState(state);
    }
    renderConfirmModal(action, data) {
        let confirmAction = '';
        switch (action) {
            case ACTIONS.APPROVE:
                if (data.status === STATUS.APPROVED_INTERNALLY) {
                    confirmAction = ACTIONS.REVERSE_APPROVAL;
                }
                else {
                    confirmAction = ACTIONS.APPROVE;
                }
                break;
            case ACTIONS.VOID:
                if (data.status === STATUS.VOID) {
                    confirmAction = ACTIONS.REVERSE_VOID;
                }
                else {
                    confirmAction = ACTIONS.VOID;
                }
                break;
            default:
                confirmAction = action;
                break;
        }
        this.setState({
            showConfirmModal: true,
            confirmAction,
            currentPayout: data,
        });
    }
    renderHeaders() {
        const { t } = this.props;
        return (React.createElement("tr", { className: "" }, Object.keys(headersByFieldNames).map((key) => {
            const icon = this.state.sort.order === 'asc' ? React.createElement(ChevronDown, null) : React.createElement(ChevronUp, null);
            const thClassNames = [''];
            let sortArrow = '';
            if (key === this.state.sort.key) {
                thClassNames.push('current-sort');
                sortArrow = React.createElement("div", { className: "cursor-pointer" }, icon);
            }
            if (key === 'select_row') {
                return (React.createElement(Checkbox, { className: "cursor-pointer p-6", id: key, checked: this.isAllItemsChecked(), onChange: this.handleToggleCheckAll, disabled: !this.state.isFinanceUser }));
            }
            return (React.createElement("th", { "data-key": key, key: key, onClick: this.onHeaderClick.bind(this, key), className: thClassNames.join(' ') },
                React.createElement("div", { className: "flex items-center min-h-6 space-x-1" },
                    React.createElement("span", null, t(headersByFieldNames[key])),
                    sortArrow)));
        })));
    }
    renderRow(payout) {
        const { t } = this.props;
        const { _id, date_created, due_date, carrier_name, total, type, status, auto_pay_enabled, number, authorized, payment_method, payment_terms, currency, } = payout;
        return (React.createElement("tr", { "data-id": _id, key: _id },
            React.createElement(PayoutCheckBoxCell, { payoutId: _id, checked: this.state.selectedPayouts.includes(_id), handleToggleCheck: this.handleToggleCheck, disabled: this.disableLineItem(payout) }),
            React.createElement(PayoutNumberCell, { data: payout }),
            React.createElement(DateCell, { data: date_created }),
            React.createElement(DateCell, { data: due_date }),
            React.createElement(RawCell, { data: carrier_name }),
            React.createElement(MoneyCell, { data: total, currency: currency }),
            React.createElement(RawCell, { data: t(sluggify(type)) }),
            React.createElement(PayoutStatusCell, { data: status }),
            React.createElement(RawCell, { data: t(sluggify(payment_method[0])) }),
            React.createElement(RawCell, { data: payment_terms }),
            React.createElement(BooleanCell, { data: auto_pay_enabled }),
            React.createElement(BooleanCell, { data: authorized && authorized.length ? authorized[0] : false }),
            React.createElement(ActionsCell, { approveAction: () => this.renderConfirmModal(ACTIONS.APPROVE, { _id, number, status }), declineAction: () => this.renderConfirmModal(ACTIONS.VOID, { _id, number, status }), disableApprove: getDisableActionsState(ACTIONS.APPROVE, status), disableDecline: getDisableActionsState(ACTIONS.VOID, status) })));
    }
    renderTitleBar() {
        const { t } = this.props;
        return (React.createElement("div", { className: "flex items-center justify-between" },
            React.createElement("h3", { className: "text-lg font-medium leading-6 text-gray-900" }, t('PAYOUT', { count: 2 })),
            SessionStore.isAdmin() && (React.createElement("button", { title: t('SEND_CSV_TO_EMAIL'), className: "flex item-center button button-hollow-green", onClick: () => this.updatePayoutReportEmailModalState(true) },
                t('SEND CSV TO EMAIL'),
                React.createElement(EmailIcon, null)))));
    }
    render() {
        const rows = this.props.payouts.map((payout) => this.renderRow(payout));
        const { t } = this.props;
        return (React.createElement(React.Fragment, null,
            React.createElement(CargomaticCard, { header: () => this.renderTitleBar() },
                React.createElement("div", { className: "-mb-4 overflow-x-auto card--no-x-padding card--no-top-padding sm:-mb-4" },
                    React.createElement("table", { className: "default-table" },
                        React.createElement("thead", null, this.renderHeaders()),
                        React.createElement("tbody", null, rows))),
                this.state.showConfirmModal && (React.createElement(ConfirmModal, { show: this.state.showConfirmModal, title: t('PAYOUT_STATUS_CONFIRMATION_WITH_VALUE', {
                        confirmStatus: t(sluggify(this.state.confirmAction)),
                        confirmAction: [
                            ACTIONS.REVERSE_APPROVAL,
                            ACTIONS.REVERSE_VOID,
                        ].includes(this.state.confirmAction)
                            ? t('OF')
                            : '',
                        payoutNumber: this.state.currentPayout.number,
                    }), onConfirm: this.onConfirmClick, onHide: this.onCancelClick, message: t('ARE_YOU_SURE'), hideButtonText: t('CANCEL'), affirmative: t('OK') }))),
            React.createElement(SendRateCardEmailModal, { modalTitle: "Email Payout Report", show: this.state.modal.showPayoutReportEmailModal, onHide: () => this.updatePayoutReportEmailModalState(false), onSend: this.handleEmailSubmit, defaultRecipient: this.getPayoutReportCardEmailRecipientList() })));
    }
}
export default withTranslation()(PayoutList);
